import React, { useRef, useEffect, useState } from 'react';
import { Stage, Layer, Rect, Circle, Text, Group, Image as KonvaImage } from 'react-konva';

const DEFAULTS = { shape: 'square', width: 70, height: 70 };
const DEFAULT_PLAN_DIMS = { width: 1200, height: 800 };

const TABLE_COLORS = {
    config: {
        normal: '#0d6efd',
        selected: '#fd7e14',
    },
    select: {
        free: '#198754',
        upcoming: '#ffc107',
        ongoing: '#6c757d',
        unavailable: '#dc3545',
    },
};

const getActiveReservation = (table, reservations) => {
    const now = new Date();
    const tableRes = reservations.filter(r => Number(r.tbl_id) === Number(table.id));
    const ongoing = tableRes.find(r => {
        const start = new Date(r.date_debut);
        const end = r.date_fin ? new Date(r.date_fin) : null;
        return start <= now && (end === null || end >= now);
    });
    if(ongoing) return ongoing;
    return tableRes
        .filter(r => new Date(r.date_debut) > now)
        .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut))[0] || null;
};

const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const getTableFill = (table, mode, selectedId, reservations, availableTableIds, availableDates) => {
    if (table.is_obstacle) {
        return selectedId === table.id ? TABLE_COLORS.config.selected : (table.position?.color || '#000000');
    }
    if (mode === 'config') {
        return selectedId === table.id ? TABLE_COLORS.config.selected : TABLE_COLORS.config.normal;
    }
    if (availableTableIds !== null) {
        if (availableTableIds.includes(Number(table.id))) return TABLE_COLORS.select.free;
        // Not available: distinguish yellow (upcoming in slot) vs red (already ongoing)
        if (availableDates) {
            const slotStart = new Date(availableDates.debut);
            const slotEnd = new Date(availableDates.fin);
            const tableRes = reservations.filter(r => Number(r.tbl_id) === Number(table.id));
            const hasUpcomingInSlot = tableRes.some(r => {
                const start = new Date(r.date_debut);
                return start > slotStart && start < slotEnd;
            });
            if (hasUpcomingInSlot) return TABLE_COLORS.select.upcoming;
        }
        return TABLE_COLORS.select.unavailable;
    }
    const now = new Date();
    const tableReservations = reservations.filter(r => Number(r.tbl_id) === Number(table.id));
    if (tableReservations.some(r => {
        const start = new Date(r.date_debut);
        const end = r.date_fin ? new Date(r.date_fin) : null;
        return start <= now && (end === null || end >= now);
    })) return TABLE_COLORS.select.ongoing;
    if (tableReservations.some(r => new Date(r.date_debut) > now)) return TABLE_COLORS.select.upcoming;
    return TABLE_COLORS.select.free;
};

const PlanTableCanvas = ({
    tables = [],
    mode = 'config',
    selectedId = null,
    onSelect,
    onDragEnd,
    reservations = [],
    availableTableIds = null,
    availableDates = null,
    dimensions = null,
    maxHeight = null,
    backgroundImage = null,
}) => {
    const containerRef = useRef(null);
    const stageRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(800);
    const [bgImg, setBgImg] = useState(null);

    useEffect(() => {
        if (!backgroundImage) { setBgImg(null); return; }
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setBgImg(img);
        img.src = backgroundImage;
    }, [backgroundImage]);

    const planDims = dimensions || DEFAULT_PLAN_DIMS;
    const scaleByWidth = containerWidth / planDims.width;
    const scaleByHeight = maxHeight ? maxHeight / planDims.height : Infinity;
    const scale = Math.min(scaleByWidth, scaleByHeight);
    const stageWidth = Math.round(planDims.width * scale);
    const stageHeight = Math.round(planDims.height * scale);

    useEffect(() => {
        const observer = new ResizeObserver(entries => {
            const width = entries[0]?.contentRect?.width;
            if (width > 0) setContainerWidth(width);
        });
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    const setCursor = (cursor) => {
        if (stageRef.current) {
            stageRef.current.container().style.cursor = cursor;
        }
    };

    const handleStageClick = (e) => {
        if (e.target === e.target.getStage() && onSelect) {
            onSelect(null);
        }
    };

    return (
        <div
            ref={containerRef}
            style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}
        >
            <div style={{ border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden', flexShrink: 0, background: '#e9ecef' }}>
            <Stage
                ref={stageRef}
                width={stageWidth}
                height={stageHeight}
                scale={{ x: scale, y: scale }}
                onClick={handleStageClick}
                onTap={handleStageClick}
            >
                <Layer>
                    {bgImg && (
                        <KonvaImage
                            image={bgImg}
                            x={0}
                            y={0}
                            width={planDims.width}
                            height={planDims.height}
                            listening={false}
                        />
                    )}
                    {tables.map(table => {
                        const x = table.position?.x ?? 50;
                        const y = table.position?.y ?? 50;
                        const shape = table.position?.shape || DEFAULTS.shape;
                        const w = table.position?.width || DEFAULTS.width;
                        const h = table.position?.height || DEFAULTS.height;
                        const fill = getTableFill(table, mode, selectedId, reservations, availableTableIds, availableDates);
                        const isSelected = selectedId === table.id;

                        const shadowProps = {
                            shadowBlur: isSelected ? 14 : 4,
                            shadowColor: 'rgba(0,0,0,0.25)',
                            shadowOffsetY: isSelected ? 0 : 2,
                        };

                        const isObstacle = !!table.is_obstacle;
                        const activeRes = (mode === 'select' && !isObstacle) ? getActiveReservation(table, reservations) : null;
                        const clientName = activeRes?.client ? activeRes.client.name : null;
                        const resTime = activeRes ? formatTime(activeRes.date_debut) : null;
                        const resNbPersonne = activeRes?.nb_personne ?? null;
                        const hasResInfo = !!(clientName || resTime);

                        const fontSizeNum = Math.max(12, Math.min(Math.round(w * (hasResInfo ? 0.22 : 0.3)), hasResInfo ? 18 : 24));
                        const fontSizePers = Math.max(9, Math.min(Math.round(w * 0.15), 12));
                        const fontSizeInfo = Math.max(9, Math.min(Math.round(w * 0.14), 11));

                        return (
                            <Group
                                key={table.id}
                                x={x}
                                y={y}
                                draggable={mode === 'config'}
                                onClick={(e) => {
                                    e.cancelBubble = true;
                                    if (onSelect && (!isObstacle || mode === 'config')) onSelect(table);
                                }}
                                onTap={(e) => {
                                    e.cancelBubble = true;
                                    if (onSelect && (!isObstacle || mode === 'config')) onSelect(table);
                                }}
                                onDragEnd={(e) => {
                                    if (onDragEnd) onDragEnd(table.id, { x: e.target.x(), y: e.target.y() });
                                }}
                                onMouseEnter={() => setCursor(mode === 'config' ? 'grab' : (isObstacle ? 'default' : 'pointer'))}
                                onMouseLeave={() => setCursor('default')}
                                onDragStart={() => setCursor('grabbing')}
                            >
                                {shape === 'circle' ? (
                                    <Circle
                                        x={w / 2}
                                        y={h / 2}
                                        radius={Math.min(w, h) / 2}
                                        fill={fill}
                                        {...shadowProps}
                                    />
                                ) : (
                                    <Rect
                                        width={w}
                                        height={h}
                                        fill={fill}
                                        cornerRadius={8}
                                        {...shadowProps}
                                    />
                                )}
                                {!isObstacle && (
                                    <Text
                                        text={String(table.numero)}
                                        width={w}
                                        y={h * (hasResInfo ? 0.05 : 0.15)}
                                        align="center"
                                        fontSize={fontSizeNum}
                                        fontStyle="bold"
                                        fill="white"
                                    />
                                )}
                                {!isObstacle && (
                                    <Text
                                        text={`${table.nb_personne}p`}
                                        width={w}
                                        y={h * (hasResInfo ? 0.30 : 0.58)}
                                        align="center"
                                        fontSize={fontSizePers}
                                        fill="rgba(255,255,255,0.85)"
                                    />
                                )}
                                {resTime && (
                                    <Text
                                        text={resTime}
                                        width={w}
                                        y={h * 0.52}
                                        align="center"
                                        fontSize={fontSizeInfo}
                                        fontStyle="bold"
                                        fill="white"
                                    />
                                )}
                                {clientName && (
                                    <Text
                                        text={clientName}
                                        width={w}
                                        y={h * (resNbPersonne ? 0.63 : 0.70)}
                                        align="center"
                                        fontSize={fontSizeInfo}
                                        fill="rgba(255,255,255,0.9)"
                                        ellipsis
                                    />
                                )}
                                {resNbPersonne && (
                                    <Text
                                        text={`${resNbPersonne}p`}
                                        width={w}
                                        y={h * 0.80}
                                        align="center"
                                        fontSize={fontSizeInfo}
                                        fill="rgba(255,255,255,0.75)"
                                    />
                                )}
                            </Group>
                        );
                    })}
                </Layer>
            </Stage>
            </div>
        </div>
    );
};

export default PlanTableCanvas;